import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, buildQuery, errorMessage, type Activation, type Page, type Product } from '../lib/api';
import { useAsync, useDebounced } from '../lib/hooks';
import { fmtDateTime, fmtRelative, shortFp } from '../lib/format';
import { ActionButton, ErrorAlert, PageHeader, Pagination, Spinner } from '../components/ui';
import { useT } from '../i18n';

export function MachinesPage() {
  const t = useT();
  const [q, setQ] = useState('');
  const dq = useDebounced(q);
  const [productId, setProductId] = useState('');
  const [active, setActive] = useState('true');
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const products = useAsync(() => api.get<Product[]>('/v1/admin/products'), []);
  const list = useAsync(
    () => api.get<Page<Activation>>(`/v1/admin/activations${buildQuery({ q: dq, product_id: productId, active, page, page_size: 25 })}`),
    [dq, productId, active, page],
  );
  return (
    <>
      <PageHeader title={t('machines.title')} subtitle={t('machines.subtitle')} />
      <ErrorAlert error={list.error ?? error} />
      <div className="card">
        <div className="toolbar">
          <input className="grow" placeholder={t('machines.searchPlaceholder')} value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
          <select value={productId} onChange={(e) => { setProductId(e.target.value); setPage(1); }} aria-label={t('common.product')}>
            <option value="">{t('common.allProducts')}</option>
            {products.data?.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <select value={active} onChange={(e) => { setActive(e.target.value); setPage(1); }} aria-label={t('common.status')}>
            <option value="true">{t('machines.active')}</option>
            <option value="false">{t('machines.inactive')}</option>
            <option value="">{t('machines.all')}</option>
          </select>
          {list.loading && <Spinner />}
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{t('machines.colMachine')}</th>
                <th>{t('machines.colLicense')}</th>
                <th>{t('common.customer')}</th>
                <th>{t('machines.colOs')}</th>
                <th>{t('machines.colLastSeen')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {list.data?.items.length === 0 && <tr><td colSpan={6} className="empty">{t('common.noResults')}</td></tr>}
              {list.data?.items.map((a) => (
                <tr key={a.id}>
                  <td>
                    <div>{a.machine_name ?? '—'} {a.offline && <span className="badge info plain">{t('licenseDetail.offlineTag')}</span>}</div>
                    <div className="mono muted" title={a.fingerprint}>{shortFp(a.fingerprint)}</div>
                  </td>
                  <td>
                    <Link className="key" to={`/licencias/${a.license_id}`}>{a.license_key}</Link>
                    <div className="muted" style={{ fontSize: 12 }}>{a.product_code}</div>
                  </td>
                  <td>{a.customer_name ?? '—'}</td>
                  <td>
                    <div>{a.os ?? '—'}</div>
                    <div className="muted" style={{ fontSize: 12 }}>{a.app_version ? `v${a.app_version}` : ''}</div>
                  </td>
                  <td title={fmtDateTime(a.last_seen_at)}>
                    {a.active ? fmtRelative(a.last_seen_at) : t('machines.deactivatedAgo', { when: fmtRelative(a.deactivated_at) })}
                  </td>
                  <td className="num">
                    {a.active && (
                      <ActionButton
                        className="btn btn-sm btn-danger"
                        confirm={t('machines.deactivateConfirm', { name: a.machine_name ?? t('licenseDetail.thisMachine') })}
                        onAction={async () => {
                          try {
                            await api.post(`/v1/admin/activations/${a.id}/deactivate`);
                            await list.reload();
                          } catch (err) {
                            setError(errorMessage(err));
                          }
                        }}
                      >
                        {t('licenseDetail.deactivate')}
                      </ActionButton>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {list.data && <Pagination page={page} pageSize={25} total={list.data.total} onPage={setPage} />}
      </div>
    </>
  );
}
